import React, { useCallback, useMemo, useRef, useState } from 'react';
import { chromosomeApi, useGetSettingsQuery } from 'src/services/chromosomeApi';
import { RootState, useDispatch, useSelector } from 'src/store';
import { setBandFile, setCytoBandFile } from 'src/reducers/BuildRenderRequestSlice';

type Mode = 'existing' | 'upload';

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12M8 8l4-4 4 4" />
  </svg>
);

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kb`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const BandsFileUpload = () => {
  const dispatch = useDispatch();
  const { data } = useGetSettingsQuery();
  const { bandFile, cytoBandFile } = useSelector((state: RootState) => state.buildRenderRequest);
  const [triggerDownload] = chromosomeApi.useLazyDownloadQuery();

  const [mode, setMode] = useState<Mode>('existing');
  const [isDragging, setIsDragging] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const existingBands = useMemo(() => data?.availableBands || [], [data?.availableBands]);

  const selectedExisting = useMemo(
    () => existingBands.find(f => f.originalFileName === bandFile),
    [existingBands, bandFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) dispatch(setCytoBandFile(file));
  }, [dispatch]);

  const onBrowse = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) dispatch(setCytoBandFile(file));
    e.target.value = '';
  }, [dispatch]);

  const handleExistingChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setBandFile(e.target.value));
  }, [dispatch]);

  const clearAll = useCallback(() => {
    dispatch(setCytoBandFile(undefined));
    dispatch(setBandFile(''));
  }, [dispatch]);

  const handleModeChange = useCallback((newMode: Mode) => {
    setMode(newMode);
    clearAll();
  }, [clearAll]);

  const handleDownload = useCallback(async (fileName: string) => {
    try {
      setDownloadingFile(fileName);
      const result = await triggerDownload(fileName);
      if (result.data && result.data.size > 0) {
        const url = window.URL.createObjectURL(result.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
      }
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingFile(null);
    }
  }, [triggerDownload]);

  return (
    <>
      <div className="band-tabs">
        <button
          type="button"
          className={`band-tab${mode === 'existing' ? ' active' : ''}`}
          onClick={() => handleModeChange('existing')}
        >
          Choose existing
        </button>
        <button
          type="button"
          className={`band-tab${mode === 'upload' ? ' active' : ''}`}
          onClick={() => handleModeChange('upload')}
        >
          Upload
        </button>
      </div>

      {mode === 'existing' ? (
        <>
          <select
            className="select-field"
            value={bandFile}
            onChange={handleExistingChange}
          >
            <option value="">— Choose —</option>
            {existingBands.map(f => (
              <option key={f.originalFileName} value={f.originalFileName}>
                {f.originalFileName} [{f.size}]
              </option>
            ))}
          </select>
          {selectedExisting && (
            <div className="file-loaded" style={{ marginTop: 8 }}>
              <div>
                <div className="fname">{selectedExisting.name}</div>
                <div className="fmeta">
                  {selectedExisting.lastModified} · {selectedExisting.size} ·{' '}
                  <button
                    type="button"
                    onClick={() => handleDownload(selectedExisting.name)}
                    disabled={downloadingFile === selectedExisting.name}
                    style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, fontSize: 'inherit', fontFamily: 'inherit' }}
                  >
                    {downloadingFile === selectedExisting.name ? 'Downloading…' : 'Download'}
                  </button>
                </div>
              </div>
              <button type="button" className="fclear" onClick={clearAll} title="Clear selection">✕</button>
            </div>
          )}
        </>
      ) : (
        <>
          {cytoBandFile ? (
            <div className="file-loaded">
              <div>
                <div className="fname">{cytoBandFile.name}</div>
                <div className="fmeta">{formatSize(cytoBandFile.size)}</div>
              </div>
              <button type="button" className="fclear" onClick={() => dispatch(setCytoBandFile(undefined))} title="Remove file">✕</button>
            </div>
          ) : null}
          <div
            className={`drop-zone${isDragging ? ' drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              onChange={onBrowse}
              hidden
              accept=".txt,.csv,.tsv"
            />
            <UploadIcon />
            <p>Drag &amp; drop or click to upload a .txt / .tsv</p>
          </div>
        </>
      )}
    </>
  );
};
